/**
 * Client Portal Service
 * Manages prospect clients: deposits, timers, site previews, access codes
 * Firestore collection: 'clients'
 */

import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';

const CLIENTS_COLLECTION = 'clients';
const DEFAULT_TIMER_HOURS = 48;

// ─── Helpers ───────────────────────────────────────────────

/** Generate a 6-character alphanumeric access code */
const generateAccessCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no I/1/O/0 for clarity
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

/** Check if a timer has expired */
const isTimerExpired = (timerExpiresAt) => {
  if (!timerExpiresAt) return true;
  const expiry = timerExpiresAt.toDate ? timerExpiresAt.toDate() : new Date(timerExpiresAt);
  return new Date() > expiry;
};

/** Get remaining time in human-readable format */
const getTimeRemaining = (timerExpiresAt) => {
  if (!timerExpiresAt) return { expired: true, display: 'Expired' };
  const expiry = timerExpiresAt.toDate ? timerExpiresAt.toDate() : new Date(timerExpiresAt);
  const now = new Date();
  const diff = expiry - now;

  if (diff <= 0) return { expired: true, display: 'Expired', ms: 0 };

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return {
    expired: false,
    hours,
    minutes,
    seconds,
    ms: diff,
    display: `${hours}h ${minutes}m ${seconds}s`
  };
};

// ─── CRUD Operations ───────────────────────────────────────

/**
 * Create a new client record after demo
 */
export const createClient = async (clientData) => {
  try {
    const clientRef = doc(collection(db, CLIENTS_COLLECTION));
    const accessCode = generateAccessCode();

    const client = {
      id: clientRef.id,
      // Company info
      companyName: clientData.companyName || '',
      contactName: clientData.contactName || '',
      contactEmail: clientData.contactEmail?.toLowerCase() || '',
      contactPhone: clientData.contactPhone || '',
      industry: clientData.industry || '',
      website: clientData.currentWebsite || '',

      // Access
      accessCode,
      accessEnabled: false, // enabled after deposit

      // Deposit
      requireDeposit: false, // admin toggle — off by default
      depositPaid: false,
      depositAmount: 5.00,
      depositDate: null,
      stripePaymentIntentId: null,

      // Timer
      timerExpiresAt: null, // set when deposit is paid
      timerHours: DEFAULT_TIMER_HOURS,
      timerExtensions: [],

      // Project
      projectStatus: 'pending_deposit', // pending_deposit | active | in_progress | review | completed | expired
      sitePreviewUrl: clientData.sitePreviewUrl || '',
      projectNotes: clientData.projectNotes || '',
      projectType: clientData.projectType || 'website', // website | ai_integration | both

      // Services requested
      servicesRequested: clientData.servicesRequested || [],

      // Metadata
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: clientData.createdBy || 'admin',
      demoDate: clientData.demoDate || Timestamp.now(),
      lastAccessedAt: null,
    };

    await setDoc(clientRef, client);
    return { id: clientRef.id, ...client, accessCode };
  } catch (error) {
    console.error('Error creating client:', error);
    throw error;
  }
};

/**
 * Get a single client by ID
 */
export const getClient = async (clientId) => {
  try {
    const docRef = doc(db, CLIENTS_COLLECTION, clientId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error getting client:', error);
    throw error;
  }
};

/**
 * Get client by email + access code (for login)
 */
export const getClientByCredentials = async (email, accessCode) => {
  try {
    const q = query(
      collection(db, CLIENTS_COLLECTION),
      where('contactEmail', '==', email.toLowerCase()),
      where('accessCode', '==', accessCode.toUpperCase()),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const clientDoc = snapshot.docs[0];
    return { id: clientDoc.id, ...clientDoc.data() };
  } catch (error) {
    console.error('Error authenticating client:', error);
    throw error;
  }
};

/**
 * Get all clients (admin)
 */
export const getAllClients = async (limitCount = 50) => {
  try {
    const q = query(
      collection(db, CLIENTS_COLLECTION),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting all clients:', error);
    throw error;
  }
};

/**
 * Get clients by status
 */
export const getClientsByStatus = async (status) => {
  try {
    const q = query(
      collection(db, CLIENTS_COLLECTION),
      where('projectStatus', '==', status),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting clients by status:', error);
    throw error;
  }
};

/**
 * Update a client record
 */
export const updateClient = async (clientId, updates) => {
  try {
    const docRef = doc(db, CLIENTS_COLLECTION, clientId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating client:', error);
    throw error;
  }
};

/**
 * Delete a client
 */
export const deleteClient = async (clientId) => {
  try {
    await deleteDoc(doc(db, CLIENTS_COLLECTION, clientId));
    return true;
  } catch (error) {
    console.error('Error deleting client:', error);
    throw error;
  }
};

// ─── Deposit Operations ────────────────────────────────────

/**
 * Mark deposit as paid and activate timer
 */
export const activateClientAfterDeposit = async (clientId, stripePaymentIntentId) => {
  try {
    const timerExpiresAt = Timestamp.fromDate(
      new Date(Date.now() + DEFAULT_TIMER_HOURS * 60 * 60 * 1000)
    );

    await updateClient(clientId, {
      depositPaid: true,
      depositDate: serverTimestamp(),
      stripePaymentIntentId,
      accessEnabled: true,
      projectStatus: 'active',
      timerExpiresAt,
    });

    return { timerExpiresAt };
  } catch (error) {
    console.error('Error activating client:', error);
    throw error;
  }
};

// ─── Timer Operations ──────────────────────────────────────

/**
 * Extend a client's timer (admin)
 */
export const extendClientTimer = async (clientId, additionalHours, reason = '') => {
  try {
    const client = await getClient(clientId);
    if (!client) throw new Error('Client not found');

    const currentExpiry = client.timerExpiresAt?.toDate
      ? client.timerExpiresAt.toDate()
      : new Date(client.timerExpiresAt);

    // If timer already expired, extend from now
    const baseTime = currentExpiry > new Date() ? currentExpiry : new Date();
    const newExpiry = Timestamp.fromDate(
      new Date(baseTime.getTime() + additionalHours * 60 * 60 * 1000)
    );

    const extension = {
      extendedAt: new Date().toISOString(),
      additionalHours,
      reason,
      previousExpiry: currentExpiry.toISOString(),
      newExpiry: newExpiry.toDate().toISOString(),
    };

    await updateClient(clientId, {
      timerExpiresAt: newExpiry,
      timerExtensions: [...(client.timerExtensions || []), extension],
      accessEnabled: true,
      projectStatus: client.projectStatus === 'expired' ? 'active' : client.projectStatus,
    });

    return { newExpiry, extension };
  } catch (error) {
    console.error('Error extending timer:', error);
    throw error;
  }
};

/**
 * Expire a client's access (called when timer hits zero)
 */
export const expireClientAccess = async (clientId) => {
  try {
    await updateClient(clientId, {
      accessEnabled: false,
      projectStatus: 'expired',
    });
    return true;
  } catch (error) {
    console.error('Error expiring client access:', error);
    throw error;
  }
};

/**
 * Toggle client access on/off (admin override)
 */
export const toggleClientAccess = async (clientId) => {
  try {
    const client = await getClient(clientId);
    if (!client) throw new Error('Client not found');

    await updateClient(clientId, {
      accessEnabled: !client.accessEnabled,
    });

    return !client.accessEnabled;
  } catch (error) {
    console.error('Error toggling client access:', error);
    throw error;
  }
};

/**
 * Record client site access
 */
export const recordClientAccess = async (clientId) => {
  try {
    await updateClient(clientId, {
      lastAccessedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error recording access:', error);
  }
};

/**
 * Update project status
 */
export const updateProjectStatus = async (clientId, status, notes = '') => {
  try {
    const updates = { projectStatus: status };
    if (notes) updates.projectNotes = notes;
    await updateClient(clientId, updates);
    return true;
  } catch (error) {
    console.error('Error updating project status:', error);
    throw error;
  }
};

/**
 * Update site preview URL
 */
export const updateSitePreviewUrl = async (clientId, url) => {
  try {
    await updateClient(clientId, { sitePreviewUrl: url });
    return true;
  } catch (error) {
    console.error('Error updating preview URL:', error);
    throw error;
  }
};

// ─── Stats (Admin) ─────────────────────────────────────────

/**
 * Get client statistics for admin dashboard
 */
export const getClientStats = async () => {
  try {
    const allClients = await getAllClients(200);
    const now = new Date();

    return {
      total: allClients.length,
      active: allClients.filter(c => c.projectStatus === 'active' || c.projectStatus === 'in_progress').length,
      pendingDeposit: allClients.filter(c => c.projectStatus === 'pending_deposit').length,
      expired: allClients.filter(c => c.projectStatus === 'expired').length,
      completed: allClients.filter(c => c.projectStatus === 'completed').length,
      depositsPaid: allClients.filter(c => c.depositPaid).length,
      totalRevenue: allClients.filter(c => c.depositPaid).reduce((sum, c) => sum + (c.depositAmount || 5), 0),
      expiringWithin24h: allClients.filter(c => {
        if (!c.timerExpiresAt || c.projectStatus === 'expired') return false;
        const expiry = c.timerExpiresAt.toDate ? c.timerExpiresAt.toDate() : new Date(c.timerExpiresAt);
        const diff = expiry - now;
        return diff > 0 && diff < 24 * 60 * 60 * 1000;
      }).length,
    };
  } catch (error) {
    console.error('Error getting client stats:', error);
    return { total: 0, active: 0, pendingDeposit: 0, expired: 0, completed: 0, depositsPaid: 0, totalRevenue: 0, expiringWithin24h: 0 };
  }
};

// ─── Approval Operations ──────────────────────────────────

/**
 * Update approval checkbox for a client
 */
export const updateApprovalBox = async (clientId, boxNumber, checked, adminUid = '') => {
  try {
    const updates = { [`approvalBox${boxNumber}`]: checked };

    // If all 3 boxes now checked, mark as approved
    if (checked) {
      const client = await getClient(clientId);
      if (!client) throw new Error('Client not found');

      const box1 = boxNumber === 1 ? checked : !!client.approvalBox1;
      const box2 = boxNumber === 2 ? checked : !!client.approvalBox2;
      const box3 = boxNumber === 3 ? checked : !!client.approvalBox3;

      if (box1 && box2 && box3) {
        updates.status = 'approved';
        updates.approvedAt = serverTimestamp();
        updates.approvedBy = adminUid;
      }
    }

    await updateClient(clientId, updates);
    return true;
  } catch (error) {
    console.error('Error updating approval box:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time client updates (admin)
 * Returns an unsubscribe function
 */
export const subscribeToClients = (callback, limitCount = 100) => {
  const q = query(
    collection(db, CLIENTS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  return onSnapshot(q, (snapshot) => {
    const clients = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(clients);
  }, (error) => {
    console.error('Error subscribing to clients:', error);
  });
};

// Export helpers
export { generateAccessCode, isTimerExpired, getTimeRemaining, DEFAULT_TIMER_HOURS };
